import { HistoryTree } from "components/historyTree/historyTree.tsx";
import { useEffect, useRef, useState } from "react";
import { fetchWithAuth } from "shared/api/fetch-with-auth.ts";
import { useSession, useUser } from "@clerk/clerk-react";
import { Task } from "shared/types/task.ts";
import { Avatar, Button, Chip, Spinner, Textarea } from "@heroui/react";
import { Messages, SendMessageRound } from "assets/icons";
import { API_URL } from "constants/index";
import { ClerkUser } from "shared/types/user";
import { useUsers } from "shared/contexts/users.context";
import ChatterSkeleton from "shared/skeletons/chatter.skeleton";
import { toast } from "react-toastify";

export enum NoteType {
  ACTIVITY_LOG = 'activityLog',
  MESSAGE = 'message'
}

export enum ResourceType {
  TASK = 'task',
  BUILDING = 'building',
  HELPDESK = 'helpdesk'
}

export enum ActivityLogType {
  ADD = 'ADD',
  EDIT = 'EDIT',
  CONNECT_RESOURCE = 'CONNECT_RESOURCE',
  MESSAGE = 'MESSAGE'
}

export interface HistoryTreeData {
  id: number;
  content: string;
  noteType: NoteType;
  resourceType: ResourceType;
  resourceId: number;
  activityLogType?: ActivityLogType;
  initiator: string;
  receiver?: string;
  createdAt: Date;
  tag: string;
}

interface ChatterProps {
  task: Task;
}

const Chatter = ({ task }: ChatterProps) => {
  const [data, setData] = useState<HistoryTreeData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { session } = useSession();
  const [input, setInput] = useState<string>('');
  const { user } = useUser();
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [taggedUsers, setTaggedUsers] = useState<ClerkUser[]>([]);
  const { users } = useUsers();
  const userDropdownRef = useRef<HTMLInputElement | null>(null);
  const searchUserRef = useRef<HTMLInputElement | null>(null);
  const [isSending, setIsSending] = useState(false);


  useEffect(() => {
    if (isUserDropdownOpen) {
      searchUserRef.current?.focus();
    }
  }, [isUserDropdownOpen]);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);  // <-- Start loading
      const response = await fetchWithAuth(`${API_URL}/notes/?resourceType=task&resourceId=${task.id}`, { method: 'GET' }, session);
      const data = await response.json();
      setData(data);
      setIsLoading(false);  // <-- End loading
    }
    fetchData();
  }, [session, task.id]);

  const isSameUser = (userId: string) => {
    return userId == user?.id;
  }


  const getUserImageUrl = (user: any) => {
    if (!user) return null;
    return user.image_url || user.imageUrl || null;
  };

  const handleInputKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { // Check if the Shift key is not pressed
      sendMessage();
      e.preventDefault();  // to prevent the default behavior of the Enter key
    }
  };

  const sendMessage = async () => {
    if (!input || input.length == 0) toast.error('Je moet een bericht invullen voor je kan verzenden.')
    setIsSending(true);
    const response = await fetchWithAuth(`${API_URL}/notes`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        content: input,
        noteType: NoteType.MESSAGE,
        resourceType: ResourceType.TASK,
        resourceId: task.id,
        initiator: user,
        receiver: task.assignee,
        taggedUsers: taggedUsers
      })
    }, session);
    const resource = await response.json();
    setData(prev => [resource, ...prev]);
    setInput('');
    setTaggedUsers([]);
    setIsSending(false);
  };


  const handleInputChange = (e: any) => {
    setInput(e.target.value);
    if (e.target.value.endsWith('@')) {
      setIsUserDropdownOpen(true);
    }
  };

  const selectUser = (user: ClerkUser) => {
    setInput(prevInput => `${prevInput}${user.first_name} `);
    setTaggedUsers([...taggedUsers, user]);
    setIsUserDropdownOpen(false);
  };

  const handleClickOutside = (e: any) => {
    if (userDropdownRef.current && !userDropdownRef.current?.contains(e.target)) {
      setIsUserDropdownOpen(false);
    }
  };

  return (
    <>
      <div className='flex items-center gap-2'>
        <Messages />
        <p>Chatter</p>
      </div>


      <div className='bg-secondary rounded-md p-4'>

        {isLoading ? <ChatterSkeleton /> : (
          <>
            <div className='flex items-center gap-2'>
              <Avatar key={user?.id} src={user?.imageUrl} />

              <div className="relative w-full p-4">
                {/* User Dropdown */}
                {isUserDropdownOpen && (
                  <div className="bg-white rounded-md shadow-lg p-4 absolute left-0 -top-full z-50">
                    {users.map(user => (
                      <div key={user.id} className="flex items-center gap-2 cursor-pointer" onClick={() => selectUser(user)}>
                        <Avatar src={getUserImageUrl(user)} name={user.first_name} />
                        <p>{user.first_name} {user.last_name}</p>
                      </div>
                    ))}
                  </div>
                )}
                {!isSameUser(task.assignee) && (
                  <div className="mt-6 flex mb2 text-sm">
                    <label htmlFor="slackMsg" className="ml-2"><Chip>{users.find(u => u.id == task.assignee).first_name}</Chip> krijgt standaard een melding van dit bericht via slack als toegewezen gebruiker.</label>
                  </div>
                )}
                <div className="mt-2 flex mb-4 text-sm">
                  {taggedUsers && taggedUsers.length > 0 && (
                    <label htmlFor="slackMsg" className="ml-2">Je stuurt dit bericht ook naar {taggedUsers.map(u => <Chip key={u.id}>{u.first_name}</Chip>)}.</label>
                  )}
                </div>
                <div className="mt-2 flex mb-4 justify-between text-sm">
                  <Textarea
                    placeholder="Typ je bericht hier..."
                    className="w-3/4"
                    value={input}
                    onChange={handleInputChange}
                    onKeyDown={handleInputKeyPress}
                  />
                  {isSending && <Spinner label="Aan het sturen.." color="warning" />}
                  <Button className='bg-neutral-primary opacity-50 mt-4' radius='full' isIconOnly={true} onClick={sendMessage}>
                    <SendMessageRound className='text-white' />
                  </Button>
                </div>
              </div>

            </div>
            <hr />
            <HistoryTree data={data} />
          </>
        )}
      </div >
    </>
  );
}

export default Chatter;